<template>
    <TemplateDetails :get-function="getAdmin" :data="admin" :loading="adminLoading">
        <PageBack/>

        <PageHeader :title="$t('admin.title')">
            <template v-slot:actions>
                <Button v-if="temporaryUser && temporaryUser.id == admin.id" size="m" color="red" icon="cross" @click.native.prevent="unsetTemporaryUser">{{ $t('temporary_user.cta_logout') }}</Button>
                <Button v-else-if="isAdmin" size="m" color="green2" icon="user" @click.native.prevent="setTemporaryUser(admin)">{{ $t('temporary_user.cta_login') }}</Button>
                <ButtonLink v-if="modificationIsAllowed" size="m" color="black" icon="edit" :to="{ name: 'admin-edit', params: { id: id } }">
                    {{ $t("global.edit") }}
                </ButtonLink>
                <ButtonLink v-if="false" size="m" icon="cross" color="red" :to="{ name: 'admin-delete', params: { id: id } }">
                    {{ $t("global.delete") }}
                </ButtonLink>
                <Button v-if="anonymizationIsAllowed" size="m" color="red" icon="trash" @click.native.prevent="$refs.popinAnonymize.open()">
                    {{ $t('admin.anonymize.title') }}
                </Button>
            </template>
        </PageHeader>

        <Popin ref="popinAnonymize"
               :center="true"
               :title="$t('admin.anonymize.popin_title')"
               :text="$t('admin.anonymize.popin_text')">
            <template v-slot:bottom>
                <Button @click.native.prevent="$refs.popinAnonymize.close()" size="m" color="grey" icon="cross">{{ $t('global.cancel') }}</Button>
                <Button @click.native.prevent="anonymize" size="m" color="red" icon="trash" :loading="anonymizing">{{ $t('admin.anonymize.confirm') }}</Button>
            </template>
        </Popin>

        <GridContainer>
            <GridCard>
                <GridContent>
                    <GridHeader :title="$t('global.informations')"/>
                    <SectionTableInfos :object="admin" :fields="fields"/>
                </GridContent>
            </GridCard>

            <template v-slot:aside>
                <GridCard>
                    <GridContent>
                        <GridHeader :title="$t('global.roles')"/>
                        <TagList>
                            <TagType
                                    v-for="(role, i) in admin.roles"
                                    :key="i"
                                    :value="role"
                                    translation-key="roles"
                            />
                        </TagList>
                    </GridContent>
                </GridCard>
            </template>
        </GridContainer>
    </TemplateDetails>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import PageHeader from "@/components/ui/page/PageHeader";
import SectionTableInfos from "@/components/sections/SectionTableInfos";
import TemplateDetails from "@/components/templates/TemplateDetails";
import ButtonLink from "@/components/ui/button/ButtonLink";
import PageBack from "@/components/ui/page/PageBack";
import TagList from "@/components/ui/tag/TagList";
import TagType from "@/components/ui/tag/TagType";
import GridContainer from "@/components/ui/grid/GridContainer";
import GridCard from "@/components/ui/grid/GridCard";
import GridContent from "@/components/ui/grid/GridContent";
import GridHeader from "@/components/ui/grid/GridHeader";
import Button from '@/components/ui/button/Button';
import Popin from '@/components/ui/popin/Popin';

export default {
    name: "AdminPage",
    components: {
        Button,
        Popin,
        GridHeader,
        GridContent,
        GridCard,
        GridContainer,
        TagType,
        TagList,
        PageBack,
        ButtonLink,
        TemplateDetails,
        SectionTableInfos,
        PageHeader,
    },
    data() {
        return {
            fields: [
                {key: "avatar", type: "img"},
                {key: "id"},
                {key: "firstname"},
                {key: "lastname"},
                {key: "email"},
                {key: "clients", type: "client"},
                {key: "organization"},
                {key: "defaultLocale", type: "flag"},
                {key: "chatbotKey"},
            ],
            id: this.$route.params.id,
            anonymizing: false,
        };
    },
    computed: {
        ...mapState(['admin', 'adminLoading', 'temporaryUser']),
        ...mapGetters(['roleIsAllowed', 'isAdmin', 'activeUser']),
        modificationIsAllowed() {
            return this.roleIsAllowed(['ROLE_ADMIN', 'ROLE_BRAND_ADMIN', 'ROLE_SUPER_STORE', 'ROLE_SUPER_ADMIN'])
        },
        anonymizationIsAllowed() {
            if (!this.admin) return false;
            // Un collaborateur ne peut anonymiser qu'un compte de niveau inférieur ou égal au sien
            const hierarchy = ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_BRAND_ADMIN'];
            const level = (roles) => {
                const index = hierarchy.findIndex(role => roles?.includes(role));
                return index === -1 ? hierarchy.length : index;
            };
            const currentLevel = level(this.activeUser?.roles);
            if (currentLevel === hierarchy.length) return false;
            // Un super admin reste intouchable, y compris par un autre super admin
            if (this.admin.roles?.includes('ROLE_SUPER_ADMIN')) return false;
            return currentLevel <= level(this.admin.roles);
        }
    },
    methods: {
        ...mapActions(['getAdmin', 'setTemporaryUser', 'unsetTemporaryUser', 'anonymizeAdmin', 'setNotificationMessage']),
        anonymize() {
            this.anonymizing = true;
            this.anonymizeAdmin()
                .then(() => {
                    this.$refs.popinAnonymize?.close();
                    this.setNotificationMessage({ text: this.$t('admin.anonymize.success'), color: 'green' });
                    this.$router.replace({ name: 'settings-admins' });
                })
                .catch(() => {})
                .finally(() => this.anonymizing = false)
        }
    },
};
</script>
